import { useEffect, useMemo, useState } from "react";
import { FileText, Upload, Download, CheckCircle2, Clock } from "lucide-react";
import { toast } from "sonner";

import { api, apiError, fmtDate } from "../lib/api";
import { EmptyState } from "../components/EmptyState";

/**
 * FH Monthly Report archive (Owner / Head only).
 *
 * Table of reports across all FHs × months. Each row exposes:
 *  · download the generated PDF
 *  · upload the physically-signed scan (flips status to SIGNED_FILED)
 *  · download the signed scan once uploaded
 *
 * "Generate now" for a specific FH + month is offered inline as a small form
 * at the top so Owner/Head can trigger reports on demand without leaving the
 * archive.
 */
export default function FhReportsAdminPage() {
    const [rows, setRows] = useState(null);
    const [users, setUsers] = useState([]);
    const [genFh, setGenFh] = useState("");
    const [genMonth, setGenMonth] = useState(() => {
        const d = new Date(); const y = d.getFullYear(); const m = d.getMonth();
        // Default to previous month
        return `${m === 0 ? y - 1 : y}-${String(m === 0 ? 12 : m).padStart(2, "0")}`;
    });
    const [busy, setBusy] = useState(false);

    async function refresh() {
        try {
            const [rep, u] = await Promise.all([
                api.get("/fh-reports"),
                api.get("/users"),
            ]);
            setRows(rep.data || []);
            setUsers((u.data || []).filter((x) => x.role === "SITE_MANAGER"));
        } catch (e) {
            toast.error(apiError(e));
            setRows([]);
        }
    }
    useEffect(() => { refresh(); }, []);

    async function generateNow() {
        if (!genFh || !genMonth) { toast.error("Pick a facility head + month."); return; }
        setBusy(true);
        try {
            await api.post(`/fh-reports/generate`, null, { params: { fh_id: genFh, month: genMonth } });
            toast.success("Report generated");
            refresh();
        } catch (e) { toast.error(apiError(e)); }
        finally { setBusy(false); }
    }

    async function downloadGenerated(rec) {
        try {
            const res = await api.get(`/fh-reports/${rec.id}/pdf`, { responseType: "blob" });
            triggerDownload(res.data, `fh-report-${rec.fh_name || "fh"}-${rec.month}.pdf`, "application/pdf");
        } catch (e) { toast.error(apiError(e)); }
    }
    async function downloadSigned(rec) {
        try {
            const res = await api.get(`/fh-reports/${rec.id}/signed-scan`, { responseType: "blob" });
            triggerDownload(res.data, `fh-report-signed-${rec.fh_name || "fh"}-${rec.month}.pdf`, res.data.type || "application/pdf");
        } catch (e) { toast.error(apiError(e)); }
    }

    async function uploadSigned(rec, file) {
        if (!file) return;
        const form = new FormData();
        form.append("file", file);
        try {
            await api.post(`/fh-reports/${rec.id}/upload-signed`, form, { headers: { "Content-Type": "multipart/form-data" } });
            toast.success("Signed scan uploaded — filed");
            refresh();
        } catch (e) { toast.error(apiError(e)); }
    }

    const grouped = useMemo(() => {
        const g = {};
        for (const r of rows || []) {
            const key = r.month;
            g[key] = g[key] || [];
            g[key].push(r);
        }
        return Object.entries(g).sort((a, b) => b[0].localeCompare(a[0]));
    }, [rows]);

    return (
        <div className="mx-auto max-w-6xl px-4 py-6 space-y-6" data-testid="fh-reports-admin">
            <header>
                <h1 className="text-2xl font-bold text-[#1E3355]">FH monthly reports</h1>
                <p className="text-sm text-gray-600 mt-1">
                    Generate, sign, and archive the monthly performance & expense summary for every Facility Head.
                </p>
            </header>

            {/* Generate on demand */}
            <section className="bg-white rounded-xl shadow-sm border border-gray-100 p-4" data-testid="generate-now-form">
                <div className="text-sm font-semibold text-[#1E3355] mb-3">Generate a report</div>
                <div className="flex flex-wrap items-end gap-3">
                    <label className="flex-1 min-w-[200px]">
                        <div className="text-xs text-gray-500 mb-1">Facility Head</div>
                        <select
                            className="w-full border rounded-md px-3 py-2 text-sm"
                            value={genFh}
                            onChange={(e) => setGenFh(e.target.value)}
                            data-testid="gen-fh-select"
                        >
                            <option value="">Select FH…</option>
                            {users.map((u) => (<option key={u.id} value={u.id}>{u.name} ({u.email})</option>))}
                        </select>
                    </label>
                    <label>
                        <div className="text-xs text-gray-500 mb-1">Month (YYYY-MM)</div>
                        <input
                            className="border rounded-md px-3 py-2 text-sm"
                            value={genMonth}
                            onChange={(e) => setGenMonth(e.target.value)}
                            placeholder="2026-06"
                            data-testid="gen-month-input"
                        />
                    </label>
                    <button
                        onClick={generateNow}
                        disabled={busy}
                        className="px-4 py-2 bg-[#1E3355] hover:bg-[#26406b] text-white rounded-md text-sm font-semibold disabled:opacity-60"
                        data-testid="generate-btn"
                    >{busy ? "Generating…" : "Generate now"}</button>
                </div>
            </section>

            {/* Archive */}
            {rows === null ? (
                <div className="p-6 text-center text-sm text-gray-500">Loading archive…</div>
            ) : rows.length === 0 ? (
                <EmptyState icon="📄" title="No FH reports yet" subtitle="Generate a report above to populate the archive." />
            ) : (
                grouped.map(([month, recs]) => (
                    <section key={month} className="bg-white rounded-xl shadow-sm border border-gray-100" data-testid={`month-block-${month}`}>
                        <div className="p-4 border-b flex items-center justify-between">
                            <h2 className="font-semibold text-[#1E3355]">{month}</h2>
                            <span className="text-xs text-gray-500">{recs.length} report(s)</span>
                        </div>
                        <ul className="divide-y">
                            {recs.map((r) => (
                                <li key={r.id} className="p-4 flex flex-wrap items-center gap-3" data-testid={`fh-report-${r.id}`}>
                                    <div className="flex-1 min-w-[220px]">
                                        <div className="font-medium text-[#1E3355]">{r.fh_name || "(unknown FH)"}</div>
                                        <div className="text-xs text-gray-500">{r.site_name || "—"} · generated {r.generated_at ? fmtDate(r.generated_at) : "—"}</div>
                                    </div>
                                    <StatusChip status={r.status} />
                                    <button onClick={() => downloadGenerated(r)} className="px-3 py-1.5 rounded-md border border-gray-300 hover:bg-gray-50 text-xs font-semibold inline-flex items-center gap-1" data-testid={`download-generated-${r.id}`}>
                                        <Download className="w-3.5 h-3.5" /> Generated PDF
                                    </button>
                                    <label className="px-3 py-1.5 rounded-md bg-[#1E3355] hover:bg-[#26406b] text-white text-xs font-semibold inline-flex items-center gap-1 cursor-pointer" data-testid={`upload-signed-${r.id}`}>
                                        <Upload className="w-3.5 h-3.5" /> {r.status === "SIGNED_FILED" ? "Replace scan" : "Upload signed"}
                                        <input type="file" accept="application/pdf,image/jpeg,image/png" className="hidden" onChange={(e) => uploadSigned(r, e.target.files?.[0])} />
                                    </label>
                                    {r.status === "SIGNED_FILED" && (
                                        <button onClick={() => downloadSigned(r)} className="px-3 py-1.5 rounded-md border border-emerald-300 text-emerald-700 hover:bg-emerald-50 text-xs font-semibold inline-flex items-center gap-1" data-testid={`download-signed-${r.id}`}>
                                            <Download className="w-3.5 h-3.5" /> Signed scan
                                        </button>
                                    )}
                                </li>
                            ))}
                        </ul>
                    </section>
                ))
            )}
        </div>
    );
}


function StatusChip({ status }) {
    if (status === "SIGNED_FILED") {
        return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-xs font-semibold"><CheckCircle2 className="w-3 h-3" /> SIGNED & FILED</span>;
    }
    return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-xs font-semibold"><Clock className="w-3 h-3" /> AWAITING SIGNATURE</span>;
}


function triggerDownload(blob, filename, type) {
    const b = blob instanceof Blob ? blob : new Blob([blob], { type });
    const url = URL.createObjectURL(b);
    const a = document.createElement("a");
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 5000);
}
